// تنسيق
import React, { useState } from "react";
import { 
  TextField, Button, Box, Typography, Stepper, Step, StepLabel, Avatar, Alert, Paper, useMediaQuery, useTheme, CircularProgress
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from "react-router-dom";
import { saveGeneralInfo, autoSetup } from "../../services/apiService";


const steps = ["البيانات العامة", "الفروع", "الطاولات", "الخزن", "المطابخ"];

const GeneralInfo = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // ✅ دعم الهواتف المحمولة
  const isTablet = useMediaQuery(theme.breakpoints.down("md")); // ✅ دعم الأجهزة اللوحية

  const [formData, setFormData] = useState({
    name: "الفرع الرئيسي",
    main_mood: "dark",
    logo: null,
    // --- new fields ---
    banner_image: null,
    footer_image: null,
    invoice_note_printer: "",
    address: "",
    phone: "",
    // --- end new fields ---
  });

  const [logoPreview, setLogoPreview] = useState(null);
  // --- new previews ---
  const [bannerPreview, setBannerPreview] = useState(null);
  const [footerPreview, setFooterPreview] = useState(null);
  // --- end new previews ---
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, logo: file });
    setLogoPreview(URL.createObjectURL(file));
  };

  // --- new handlers for banner & footer ---
  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, banner_image: file });
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleFooterChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, footer_image: file });
    setFooterPreview(URL.createObjectURL(file));
  };
  // --- end new handlers ---

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await saveGeneralInfo(formData);
      setSuccessMessage("تم حفظ البيانات بنجاح! سيتم الانتقال الآن لإكمال الإعداد...");
      setError(null);
      setTimeout(() => navigate("/setup/branches"), 2500);
    } catch (err) {
      setError(err.message);
      setSuccessMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSetup = async () => {
    try {
      setLoading(true);
      await autoSetup();
      setSuccessMessage("تم الإعداد التلقائي بنجاح! 🎉 سيتم الانتقال الآن لبدء العمل...");
      setError(null);
      setTimeout(() => navigate('/pos'), 2500);
    } catch (error) {
      setError(error.message);
      setSuccessMessage("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 5, mb: 5 }}>
      <Paper elevation={4} sx={{ maxWidth: 600, width: "100%", p: 4, borderRadius: 3, boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)" }}>
        
        {/* ✅ تحسين الخطوات لتكون متجاوبة */}
        <Stepper activeStep={0} alternativeLabel={!isSmallScreen} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{!isTablet ? label : null}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Typography variant={isSmallScreen ? "h6" : "h5"} sx={{ mb: 3, fontWeight: "bold", textAlign: "center" }}>
          إعدادات البيانات العامة
        </Typography>

        {/* ✅ إدخال اسم الفرع */}
        <TextField
          label="اسم المطعم / الكافية"
          name="name"
          fullWidth
          value={formData.name}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        {/* ✅ اختيار الوضع الافتراضي */}
        <TextField
          select
          label="الوضع الافتراضي"
          name="main_mood"
          fullWidth
          SelectProps={{ native: true }}
          value={formData.main_mood}
          onChange={handleChange}
          sx={{ mb: 2 }}
        >
          <option value="light">فاتح</option>
          <option value="dark">داكن</option>
        </TextField>

        {/* ✅ رفع الشعار */}
        <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "column" : "row", alignItems: "center", gap: 2, mb: 2 }}>
          <Button 
            variant="contained" 
            component="label" 
            startIcon={<CloudUploadIcon />} 
            fullWidth={isSmallScreen}
          >
            رفع شعار المطعم
            <input type="file" hidden accept="image/*" onChange={handleFileChange} />
          </Button>
          {logoPreview && (
            <Avatar 
              src={logoPreview} 
              sx={{ width: isSmallScreen ? 70 : 100, height: isSmallScreen ? 70 : 100, borderRadius: 2 }} 
            />
          )}
        </Box>

        {/* --- new: banner & footer uploads --- */}
        <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: 2, mb: 2 }}>
          <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth={isSmallScreen}>
            رفع صورة البانر
            <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
          </Button>
          {bannerPreview && (
            <Avatar src={bannerPreview} variant="square" sx={{ width: isSmallScreen ? 120 : 180, height: isSmallScreen ? 60 : 90, borderRadius: 1 }} />
          )}

          <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} fullWidth={isSmallScreen}>
            رفع صورة الفوتر
            <input type="file" hidden accept="image/*" onChange={handleFooterChange} />
          </Button>
          {footerPreview && (
            <Avatar src={footerPreview} variant="square" sx={{ width: isSmallScreen ? 120 : 180, height: isSmallScreen ? 60 : 90, borderRadius: 1 }} />
          )}
        </Box>
        {/* --- end new uploads --- */}

        {/* --- new text fields --- */}
        <TextField
          label="ملاحظة الفاتورة ( للطابعة )"
          name="invoice_note_printer"
          fullWidth
          value={formData.invoice_note_printer}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        <TextField
          label="العنوان"
          name="address"
          fullWidth
          value={formData.address}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />

        <TextField
          label="الهاتف"
          name="phone"
          fullWidth
          value={formData.phone}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        {/* --- end new text fields --- */}

        {/* ✅ تحسين ترتيب الأزرار لتكون متجاوبة */}
        <Box sx={{ display: "flex", flexDirection: isSmallScreen ? "column" : "row", gap: 2, justifyContent: "center", mt: 2 }}>
          <Button variant="outlined" color="secondary" onClick={handleAutoSetup} disabled={loading} fullWidth={isSmallScreen}>
            {loading ? <CircularProgress size={20} /> : "تنفيذ الإعداد التلقائي"}
          </Button>

          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} fullWidth={isSmallScreen}>
            {loading ? <CircularProgress size={20} /> : "التالي"}
          </Button>
        </Box>

        {/* ✅ تحسين رسائل التنبيه */}
        {successMessage && (
          <Alert severity="success" sx={{ mt: 2, textAlign: "center" }}>
            {successMessage}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, textAlign: "center" }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default GeneralInfo;
